import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { surfaceShadows } from '../../surfaceStyles';
import { SettingsIcon } from './icons';
import { DialogContent } from './DialogContent';
import { DialogActions } from './DialogActions';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { CollectionBrowseScope } from '../../features/collection/useCollectionBrowseScope';
import { CollectionSession } from '@qkindex/contracts';
import { ResponsiveDialog } from './ResponsiveDialog';
import { DialogHeader } from './DialogHeader';
import { collectionManagementErrorMessage } from '../../features/collection/collectionManagementService';
import { userProfileResolverService } from '../../services/userProfileResolverService';
import { firebaseCollectionRepository } from '../../repositories/collectionRepository';

export function CollectionNameDialog({
  open,
  title,
  action,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  action: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (open) return;
    setName('');
    setBusy(false);
    setError(undefined);
  }, [open]);
  const { requestClose, closeAfterSave } = useUnsavedChangesGuard({
    scopeKey: 'app.child',
    open,
    dirty: name !== '',
    pending: busy,
    onDiscard: () => {
      setName('');
      setError(undefined);
    },
    onClose,
  });
  const requestDialogClose = () => {
    void requestClose();
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !name.trim()) return;
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit(name.trim());
      setName('');
      closeAfterSave();
    } catch (err) {
      setError(collectionManagementErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ResponsiveDialog
      open={open}
      onClose={requestDialogClose}
      aria-labelledby="collection-name-dialog-title"
    >
      <DialogHeader
        id="collection-name-dialog-title"
        title={title}
        onClose={requestDialogClose}
      />
      <DialogContent>
        <Stack
          component="form"
          id="collection-name-form"
          onSubmit={(event) => {
            void submit(event);
          }}
          spacing={2}
        >
          <TextField
            autoFocus
            fullWidth
            label="Collection name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 320 }}
            disabled={busy}
            error={Boolean(error)}
            helperText={error}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={requestDialogClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="collection-name-form"
          variant="contained"
          disabled={busy || !name.trim()}
        >
          {busy ? 'Saving…' : action}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

interface ResolvedOwnerState {
  username: string | null;
  ownerUserId?: string;
  membershipVersion: number;
}

const collectionOwnerUsername = (
  collection: CollectionSession['availableCollections']['items'][number],
  resolved?: ResolvedOwnerState,
) =>
  resolved &&
  resolved.membershipVersion === collection.membershipVersion &&
  (!collection.ownerUserId || resolved.ownerUserId === collection.ownerUserId)
    ? (resolved.username ?? collection.ownerUsername)
    : collection.ownerUsername;

export function CollectionSwitcherSheet({
  embedded = false,
  open,
  onClose,
  session,
  activeCollectionId,
  onSelect,
  onManage,
  onCreate,
  onLoadMore,
  nameDialog,
  onNameDialogChange,
  browseScope,
  multiCollection = false,
  onMultiCollectionChange,
  onBrowseScopeChange,
}: {
  embedded?: boolean;
  browseScope?: CollectionBrowseScope;
  multiCollection?: boolean;
  onMultiCollectionChange?: (enabled: boolean) => void;
  onBrowseScopeChange?: (scope: CollectionBrowseScope) => void;
  open: boolean;
  onClose: () => void;
  session?: CollectionSession;
  activeCollectionId?: string;
  onSelect: (collectionId: string) => void;
  onCreate?: (name: string) => Promise<void>;
  onManage?: (collectionId: string) => void;
  onLoadMore?: () => Promise<void>;
  nameDialog: 'create' | null;
  onNameDialogChange: (value: 'create' | null) => void;
}) {
  const [collectionFilter, setCollectionFilter] = useState('');
  const [scopeDraft, setScopeDraft] = useState<CollectionBrowseScope>({
    kind: 'all',
  });
  useEffect(() => {
    if (open && browseScope) setScopeDraft(browseScope);
  }, [open, browseScope]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [resolvedUsernames, setResolvedUsernames] = useState<
    Record<string, ResolvedOwnerState>
  >({});
  const activeNameDialog = nameDialog;
  const setNameDialog = onNameDialogChange;
  const collections = useMemo(
    () => session?.availableCollections.items || [],
    [session?.availableCollections.items],
  );
  const visibleCollections = useMemo(() => {
    const filter = collectionFilter.toLocaleLowerCase();
    return collections
      .filter(
        (entry) =>
          entry.lifecycle === 'active' &&
          `${entry.name} ${collectionOwnerUsername(entry, resolvedUsernames[entry.collectionId]) || ''}`
            .toLocaleLowerCase()
            .includes(filter),
      )
      .sort((left, right) => {
        const leftOwned = left.role === 'owner';
        const rightOwned = right.role === 'owner';
        if (leftOwned !== rightOwned) return leftOwned ? -1 : 1;
        return left.name.localeCompare(right.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });
  }, [collections, collectionFilter, resolvedUsernames]);

  useEffect(() => {
    if (!open) return;
    let active = true;

    setResolvedUsernames((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [colId, entry] of Object.entries(prev)) {
        const col = collections.find((c) => c.collectionId === colId);
        if (
          !col ||
          col.role === 'owner' ||
          col.membershipVersion !== entry.membershipVersion ||
          (col.ownerUserId &&
            entry.ownerUserId &&
            col.ownerUserId !== entry.ownerUserId)
        ) {
          delete next[colId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    const recordResolution = (
      colId: string,
      ownerUserId: string | undefined,
      membershipVersion: number,
      username: string | null,
    ) => {
      if (!active) return;
      setResolvedUsernames((prev) => {
        const current = prev[colId];
        if (!current && username === null) {
          return prev;
        }
        if (
          current &&
          current.username === username &&
          current.ownerUserId === ownerUserId &&
          current.membershipVersion === membershipVersion
        ) {
          return prev;
        }
        return {
          ...prev,
          [colId]: { username, ownerUserId, membershipVersion },
        };
      });
    };

    collections.forEach((collection) => {
      if (collection.role === 'owner') return;

      if (collection.ownerUsername) {
        recordResolution(
          collection.collectionId,
          collection.ownerUserId,
          collection.membershipVersion,
          collection.ownerUsername,
        );
      }

      const targetUserId = collection.ownerUserId;
      if (targetUserId) {
        Promise.resolve(
          userProfileResolverService.resolveUsername(
            collection.collectionId,
            targetUserId,
            collection.membershipVersion,
          ),
        )
          .then((username) => {
            recordResolution(
              collection.collectionId,
              targetUserId,
              collection.membershipVersion,
              username ?? collection.ownerUsername ?? null,
            );
          })
          .catch(() => {
            recordResolution(
              collection.collectionId,
              targetUserId,
              collection.membershipVersion,
              collection.ownerUsername ?? null,
            );
          });
      } else {
        Promise.resolve(
          firebaseCollectionRepository.get(collection.collectionId),
        )
          .then((doc) => {
            if (!active) return;
            const docOwnerUserId = doc?.ownerUserId;
            if (!docOwnerUserId) {
              recordResolution(
                collection.collectionId,
                undefined,
                collection.membershipVersion,
                collection.ownerUsername ?? null,
              );
              return;
            }
            return Promise.resolve(
              userProfileResolverService.resolveUsername(
                collection.collectionId,
                docOwnerUserId,
                collection.membershipVersion,
              ),
            )
              .then((username) => {
                recordResolution(
                  collection.collectionId,
                  docOwnerUserId,
                  collection.membershipVersion,
                  username ?? collection.ownerUsername ?? null,
                );
              })
              .catch(() => {
                recordResolution(
                  collection.collectionId,
                  docOwnerUserId,
                  collection.membershipVersion,
                  collection.ownerUsername ?? null,
                );
              });
          })
          .catch(() => {
            recordResolution(
              collection.collectionId,
              undefined,
              collection.membershipVersion,
              collection.ownerUsername ?? null,
            );
          });
      }
    });

    return () => {
      active = false;
    };
  }, [open, collections]);

  const loadMore = async () => {
    if (!onLoadMore || loadingMore) return;
    setLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <ResponsiveDialog
        embedded={embedded}
        open={open}
        onClose={onClose}
        aria-labelledby="collection-switcher-title"
      >
        <DialogHeader
          id="collection-switcher-title"
          title="Choose collection"
          onClose={onClose}
        />
        <DialogContent>
          {collections.length === 0 && (
            <Alert severity="info">
              No collections are available for this account.
            </Alert>
          )}
          {onMultiCollectionChange && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={multiCollection}
                    onChange={(_, checked) => onMultiCollectionChange(checked)}
                    inputProps={{ role: 'switch' }}
                  />
                }
                label="Browse multiple collections"
              />
            </>
          )}
          {onBrowseScopeChange && multiCollection && (
            <Button
              fullWidth
              variant={scopeDraft.kind === 'all' ? 'contained' : 'outlined'}
              onClick={() => setScopeDraft({ kind: 'all' })}
            >
              All collections
            </Button>
          )}
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-end"
            sx={{ mt: 2, mb: 1 }}
          >
            <TextField
              size="small"
              fullWidth
              label="Find collections"
              value={collectionFilter}
              onChange={(event) => setCollectionFilter(event.target.value)}
              sx={{ minWidth: 0 }}
            />
            {onCreate && (
              <Button
                variant="contained"
                onClick={() => setNameDialog('create')}
                sx={{ flexShrink: 0 }}
              >
                Create
              </Button>
            )}
          </Stack>
          <List aria-label="Available collections" sx={{ py: 0.5 }}>
            {visibleCollections.map((collection) => {
              const isShared = collection.role !== 'owner';
              const ownerUsername = collectionOwnerUsername(
                collection,
                resolvedUsernames[collection.collectionId],
              );
              return (
                <ListItemButton
                  key={collection.collectionId}
                  selected={
                    onBrowseScopeChange
                      ? scopeDraft.kind === 'all' ||
                        scopeDraft.ids.includes(collection.collectionId)
                      : collection.collectionId === activeCollectionId
                  }
                  onClick={() => {
                    if (onBrowseScopeChange && !multiCollection) {
                      onBrowseScopeChange({
                        kind: 'selected',
                        ids: [collection.collectionId],
                      });
                      onClose();
                    } else if (onBrowseScopeChange) {
                      const ids =
                        scopeDraft.kind === 'all'
                          ? collections
                              .filter((entry) => entry.lifecycle === 'active')
                              .map((entry) => entry.collectionId)
                          : scopeDraft.ids;
                      setScopeDraft({
                        kind: 'selected',
                        ids: ids.includes(collection.collectionId)
                          ? ids.filter((id) => id !== collection.collectionId)
                          : [...ids, collection.collectionId],
                      });
                    } else {
                      onSelect(collection.collectionId);
                      onClose();
                    }
                  }}
                  sx={{
                    minHeight: 72,
                    borderRadius: 1,
                    px: 1.5,
                    my: 0.5,
                    border: 0,
                    '&.Mui-selected': {
                      boxShadow: surfaceShadows.inset,
                      bgcolor: 'action.selected',
                    },
                  }}
                >
                  {onBrowseScopeChange && multiCollection && (
                    <Checkbox
                      tabIndex={-1}
                      disableRipple
                      checked={
                        scopeDraft.kind === 'all' ||
                        scopeDraft.ids.includes(collection.collectionId)
                      }
                      sx={{ mr: 1.5, flexShrink: 0 }}
                    />
                  )}
                  <ListItemText
                    sx={{ minWidth: 0 }}
                    primaryTypographyProps={{ fontWeight: 600 }}
                    secondaryTypographyProps={{ sx: { mt: 0.5 } }}
                    primary={
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'baseline',
                          gap: 0.75,
                          minWidth: 0,
                          maxWidth: '100%',
                        }}
                      >
                        <Box
                          component="bdi"
                          dir="auto"
                          sx={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {collection.name}
                        </Box>
                        {isShared && ownerUsername && (
                          <Typography
                            component="bdi"
                            dir="ltr"
                            variant="inherit"
                            color="text.secondary"
                            sx={{ fontWeight: 400, flexShrink: 0 }}
                            data-testid={`collection-owner-${collection.collectionId}`}
                          >
                            @{ownerUsername}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={`${collection.role}${collection.lifecycle !== 'active' ? ` · ${collection.lifecycle.replace('-', ' ')}` : ''}`}
                  />
                  {onManage && (
                    <IconButton
                      aria-label={`Manage collection: ${collection.name}`}
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        onManage(collection.collectionId);
                      }}
                      sx={{ ml: 1, flexShrink: 0, minWidth: 44, minHeight: 44 }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemButton>
              );
            })}
          </List>
          {onLoadMore && (
            <Button
              onClick={() => {
                void loadMore();
              }}
              disabled={loadingMore}
              sx={{ mx: 1, mb: 1 }}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </DialogContent>
        {onBrowseScopeChange && multiCollection && (
          <DialogActions>
            <Button
              onClick={() => setScopeDraft({ kind: 'selected', ids: [] })}
            >
              Clear selection
            </Button>
            <Button
              variant="contained"
              disabled={
                scopeDraft.kind === 'selected' && !scopeDraft.ids.length
              }
              onClick={() => {
                onBrowseScopeChange(scopeDraft);
                onClose();
              }}
            >
              Show results
            </Button>
          </DialogActions>
        )}
      </ResponsiveDialog>

      {onCreate && (
        <CollectionNameDialog
          open={activeNameDialog === 'create'}
          title="Create collection"
          action="Create"
          onClose={() => setNameDialog(null)}
          onSubmit={onCreate}
        />
      )}
    </>
  );
}
